/**
 * View models of the Virtual Profiler module.
 *
 * Virtual Profiler speaks snake_case and sends every instant as text. Nothing in
 * this folder does: the gateways translate once, at the boundary, and no component
 * ever sees a `string` where a date belongs or an `enqueued_at` where a name does.
 *
 * This is **not** a domain. There is no invariant to protect here — the analysis
 * domain lives in Virtual Profiler and the back-office forwards. These are the
 * shapes the screens read, and that is all they are.
 */

/**
 * The eight states of a run. Kept in the ledger's own spelling: it is an id, not a label.
 *
 * `PAUSED` is a run of a paused execution that had not started (product owner, 2026-09-29). It is
 * neither final nor in flight: it is never sent to a worker and holds no place in the queue, and it
 * waits until its execution is resumed — back to `QUEUED` — or cancelled.
 */
export type RunStatus =
  | 'QUEUED'
  | 'DISPATCHED'
  | 'RUNNING'
  | 'CANCEL_REQUESTED'
  | 'PAUSED'
  | 'SUCCEEDED'
  | 'FAILED'
  | 'CANCELLED';

export const TERMINAL_RUN_STATUSES: readonly RunStatus[] = ['SUCCEEDED', 'FAILED', 'CANCELLED'];

export function isRunFinished(status: RunStatus): boolean {
  return TERMINAL_RUN_STATUSES.includes(status);
}

export type RunSourceType = 'interview' | 'manual_analysis' | 'cv';

export type RunTrigger =
  | 'upload'
  | 'reanalyze'
  | 'bulk_reanalyze'
  | 'auto_retry'
  | 'group_rerun'
  | 'http'
  | 'replay'
  /** A run the backfill invented for a result older than the ledger. Shown differently. */
  | 'legacy';

export type RunStageStatus = 'running' | 'succeeded' | 'failed' | 'skipped';

export interface RunStage {
  name: string;
  status: RunStageStatus;
  startedAt: Date | null;
  finishedAt: Date | null;
  durationMs: number | null;
}

/**
 * Which engines ran, and what they actually resolved to.
 *
 * `resolved` stays a bag of unknowns on purpose: it is where Virtual Profiler
 * records the provider and model it picked, and it grows a key whenever an engine
 * gains an option. Narrowing it would mean a release here for each of those.
 */
export interface EngineVersions {
  toolVersion: string;
  buildSha: string;
  engines: Record<string, string>;
  resolved: Record<string, unknown>;
  /** An engine that quietly fell back instead of failing. The screen has to say so. */
  silentFallbacks: Array<{ name: string; detail: string; at: Date }>;
  degraded: boolean;
  degradedReasons: string[];
}

export interface RunConfigRef {
  /** Opaque on this side. Recomputing it is Virtual Profiler's business. */
  enginesHash: string | null;
  interviewAnalysisConfigId: number | null;
  gesturesConfigId: string | null;
  version: number | null;
  /**
   * The whole configuration the run was sealed with.
   *
   * Only on `GET /analysis-runs/:id`: every list projects it out, because it carries the entire
   * configuration document and a page of fifty would be megabytes. `null` on a run that predates
   * the versioning — nothing was recorded when it ran.
   */
  snapshot?: Record<string, unknown> | null;
}

export interface RunFailure {
  type: string;
  message: string;
  stage: string | null;
}

export interface RunCancellation {
  requestedAt: Date;
  reason: string | null;
  forced: boolean;
  /**
   * Cancelled too late: the run was already writing its result and finished anyway.
   * The screen has to say that rather than show a cancellation that did not happen.
   */
  ignoredBecauseFinishing: boolean;
}

/** View model of one analysis run. */
export interface AnalysisRun {
  id: string;
  sourceType: RunSourceType;
  sourceId: string;
  /**
   * The name of what it analysed — the video's own, as the operator named it.
   *
   * Null for an interview or a CV, which have no name of their own, for a video that no longer
   * exists, and whenever upstream could not read it: the screens then fall back to «Vídeo 307».
   */
  sourceName: string | null;
  status: RunStatus;
  attempt: number;
  trigger: RunTrigger;
  batch: string | null;
  note: string | null;
  /**
   * Who launched it as an execution: a group somebody made, by its id, or a control, by its kind.
   * Both null for a run of the video's own. Never both set.
   */
  groupId: string | null;
  controlKind: ControlKind | null;
  /**
   * Whether finishing well makes this the video's active result.
   *
   * `false` on every group re-run, which is the trap of the whole module: a group
   * can be analysed a hundred times without any of it touching what the video shows.
   */
  promoteOnSuccess: boolean;
  position: number | null;
  priority: number;
  enqueuedAt: Date | null;
  startedAt: Date | null;
  finishedAt: Date | null;
  /** Last sign of life. Silence here is how a lost run is spotted. */
  heartbeatAt: Date | null;
  /**
   * The worker that started it, by its Celery node name (`celery-worker-1@<host>`): the lane it
   * belongs to on the queue timeline. Null before it starts, and on the runs migrated from before
   * the ledger, which never recorded one.
   */
  workerId: string | null;
  currentStep: string | null;
  progress: number;
  enginesRequested: string[];
  /** Added by the dependency rules rather than asked for. Shown as such. */
  enginesAutoAdded: string[];
  enginesExecuted: string[];
  enginesSkipped: Array<{ name: string; reason: string }>;
  stages: RunStage[];
  config: RunConfigRef | null;
  engine: EngineVersions | null;
  error: RunFailure | null;
  resultId: string | null;
  cancellation: RunCancellation | null;
  /**
   * Whether upstream kept the job it queued, which is what a replay sends again.
   *
   * False on every run from before the holding pen and on every backfilled one: upstream refuses
   * to replay them, so the history does not offer it.
   */
  hasStoredJob: boolean;
  /**
   * Whether replaying it would make the result what the video shows.
   *
   * Read from the launch options upstream stored, which is what its replay reads, and not from
   * `promoteOnSuccess`: a run without stored options is replayed without promoting.
   */
  replayPromotes: boolean;
  /**
   * The failed run this one repeats, when upstream launched it by itself (`trigger: 'auto_retry'`).
   *
   * Null on every other run, and on the retries of the older error sweep, which never recorded it.
   */
  retryOf: string | null;
  /**
   * What kind of job it is, in upstream's spelling. Read for one thing: `transcription` is a
   * video's transcription listed among the runs on the queue screen, which is not an analysis
   * and has none of a run's handles (`lib/transcription-run.ts`). Absent from a Virtual Profiler
   * that does not send it.
   */
  jobType?: string | null;
}

/** A waiting run knows its 1-based place in the lane; `null` once it is on its way out. */
export interface WaitingRun extends AnalysisRun {
  queueIndex: number | null;
}

/**
 * How much room there is to run analyses.
 *
 * `capacity: null` is **not** zero: it means nobody could tell us, and the
 * dispatcher refuses to dispatch without a live worker view. Reading it as a full
 * queue is the mistake this comment exists to prevent.
 */
export interface QueueSlots {
  capacity: number | null;
  inUse: number;
  free: number | null;
  source: 'workers' | 'cap' | 'unknown';
}

export interface QueueStorage {
  collections: Array<{
    name: string;
    documents: number | null;
    sizeBytes: number | null;
  }>;
  totalSizeBytes: number | null;
  orphanChunks: number;
}

export interface QueueHealth {
  bridgeAlive: boolean;
  lastMessageAt: Date | null;
  degradedRuns: number;
  /** Ran and ended in an error. There is a result — the error — and it can be launched again. */
  failedRuns: number;
  /** The worker went silent. Not the same as an error: there is no result and none is coming. */
  lostRuns: number;
  oldestQueuedAt: Date | null;
  storage: QueueStorage | null;
}

/** What a worker says it runs. */
export interface WorkerVersions {
  toolVersion: string | null;
  buildSha: string | null;
  /** Engine → version: what a run the worker takes now is analysed with. */
  engines: Record<string, string>;
}

/** A live worker, as Celery answered for it on the last inspect. */
export interface QueueWorker {
  /** Its Celery node name: what its runs carry in `workerId`. */
  name: string;
  /** Analyses it runs at once: 1 in the one-analysis-per-worker model. */
  concurrency: number;
  /** The runs it is executing right now, by id. */
  active: string[];
  /** Null from a worker on code older than the question. */
  versions: WorkerVersions | null;
}

/** The whole queue screen in one answer: three lanes, the counters and the health strip. */
export interface AnalysisQueue {
  waiting: WaitingRun[];
  running: AnalysisRun[];
  recent: AnalysisRun[];
  slots: QueueSlots;
  /** The live workers, by name; null from a Virtual Profiler build that does not send them. */
  workers: QueueWorker[] | null;
  paused: boolean;
  health: QueueHealth;
  versions: {
    api: { toolVersion: string | null; buildSha: string | null };
    worker: EngineVersions | null;
  };
}

/** Where a waiting run is moved to. Exactly one of the three. */
export type RunPlacement = { beforeRunId: string } | { afterRunId: string } | { position: number };

/**
 * Permission to open a live connection to Virtual Profiler, for sixty seconds and once.
 *
 * The browser has no key of its own: it asks the back-office, the back-office asks Virtual
 * Profiler with its service key, and what comes back is this. `url` travels with the ticket on
 * purpose — where Virtual Profiler listens is deployment configuration, and a frontend that had
 * to know it would need rebuilding to be moved.
 */
export interface RealtimeTicket {
  ticket: string;
  url: string;
  expiresAt: Date;
}

/** Filters of the run history, on top of the page the table asks for. */
export interface RunFilters {
  sourceType?: RunSourceType;
  sourceId?: string;
  groupId?: string;
  /** The runs of a control's executions: a control is not a group, so it has its own filter. */
  controlKind?: ControlKind;
  /** Several statuses travel comma-separated, which is what the endpoint takes. */
  status?: RunStatus[];
  /**
   * Lists the video transcriptions among the runs. Only the queue screen sets it: it shows what
   * the worker does, and everywhere else this history means «the analyses».
   */
  includeTranscriptions?: boolean;
}

// ---------------------------------------------------------------------------
// The video dataset (HU-15 to HU-23)
// ---------------------------------------------------------------------------

/**
 * The states of a video row. Not a run's seven: see `Video.status`.
 *
 * `READY` is a video in the dataset that nothing has analysed yet: uploading launches nothing
 * (product owner, 2026-09-28), so it is where every upload rests once it is registered.
 */
export type VideoStatus = 'UPLOADING' | 'READY' | 'ANALYSING' | 'COMPLETED' | 'ERROR';

/**
 * How a video's transcription stands (product owner, 2026-09-28). Every uploaded video is
 * transcribed once, by a job of its own and not by an analysis; `none` is a video it was never
 * asked for, older than the job.
 */
export type TranscriptionStatus = 'none' | 'pending' | 'running' | 'done' | 'failed';

/**
 * What a labelled video is (incoherence-only labelling, 2026-10-14): `incoherent` with at least one
 * incoherence range, `coherent` marked complete with none. Unlabelled is a null, never a third value.
 */
export type VideoLabel = 'incoherent' | 'coherent';

/**
 * A video, as the gallery reads it.
 *
 * camelCase, real dates, one name per concept. The seven spellings of the duration and
 * the three of the folder are already gone by the time anything here sees a video: the
 * gateway collapses them, and so does the back-office before that.
 */
export interface Video {
  id: number;
  name: string;
  description: string | null;
  originalFilename: string;
  filesizeBytes: number | null;
  /**
   * Seconds, or null when nobody knows.
   *
   * Computed once when the video is uploaded and never recomputed, so an older row
   * simply has none. `null` reads as «sin datos» and must never be shown as 0:00.
   */
  durationSeconds: number | null;
  /**
   * The row's own status.
   *
   * **Not** the status of every analysis of this video. A group re-analysis never
   * touches the row, so a video being re-analysed as part of a group still reads
   * COMPLETED here — live state comes from the ledger (HU-23).
   */
  status: VideoStatus;
  progress: number;
  errorMessage: string | null;
  folderId: number | null;
  folderName: string | null;
  analysisConfigId: number | null;
  activeRunId: string | null;
  /**
   * Somebody marked this video: it rides at the top of the list.
   *
   * A bookmark and nothing else — no pointer moves, no run freezes, no metric changes. One list
   * for everybody: this side stores nothing about who is looking.
   */
  favourite: boolean;
  resultId: string | null;
  analysisStartedAt: Date | null;
  analysisCompletedAt: Date | null;
  createdAt: Date | null;
  updatedAt: Date | null;
  /** Signed and short-lived. Either can be null on its own. */
  thumbnailUrl: string | null;
  videoUrl: string | null;
  /** Null means nobody has labelled it, and an unlabelled video counts in no metric. */
  label: VideoLabel | null;
  /** How many incoherence ranges it has: 0 for a coherent or unlabelled video. */
  incoherenceRanges: number;
  /** Null from a gateway that does not send it, or with a status this build does not know. */
  transcriptionStatus: TranscriptionStatus | null;
  /** Who the video shows: one person at most, null when nobody assigned it to one. */
  person: VideoPerson | null;
}

/** A label to filter by: either answer, or none yet. */
export type VideoLabelFilter = VideoLabel | 'unlabelled';

/** What the gallery can be ordered by. Upstream's own names: they travel as they are. */
export type VideoSortField =
  'created_at' | 'name' | 'duration' | 'filesize' | 'analysis_completed_at';

/**
 * Everything the gallery narrows by, applied upstream.
 *
 * Server-side and never on the page: the gallery is paged, and filtering the twenty-four cards on
 * screen would call «no hay ninguno» a page that simply did not hold them. Absent means «no filter»
 * for every field.
 */
/** The person of a video, as its row carries it. */
export interface VideoPerson {
  id: number;
  name: string;
}

/** A person of the dataset: who appears in its videos. A video has one at most. */
export interface Person {
  id: number;
  name: string;
  /** How many dataset videos are assigned to them. */
  videos: number;
  /** They have a CV whose analysis finished. */
  hasCv: boolean;
  createdAt: Date | null;
  /** How many CVs were uploaded for them, and how many finished their extraction. */
  cvs: number;
  cvsCompleted: number;
  /** Notes given as reference about them or the situation. */
  contexts: number;
  interviews: number;
}

/** Where a CV's extraction stands: upstream's own spelling. */
export type PersonCvStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'ERROR';

/** A CV uploaded for a person. Never deleted: every completed one is a source of the CV rules. */
export interface PersonCv {
  id: number;
  personId: number;
  fileName: string | null;
  status: PersonCvStatus;
  createdAt: Date | null;
  completedAt: Date | null;
  error: string | null;
}

/** A CV and what was extracted from it; `data` is null until the extraction completed. */
export interface PersonCvDetail {
  cv: PersonCv;
  data: Record<string, unknown> | null;
}

/** What deleting a person did: all their data gone; their analysis history kept. */
export interface PersonDeletion {
  id: number;
  videosDeleted: number;
  cvsDeleted: number;
  contextsDeleted: number;
  /** Interviews they were unlinked from; the interviews themselves stay. */
  interviewsUnlinked: number;
}

/** A note about the person or the situation, read by the coherence rules of origin «Contexto». */
export interface PersonContext {
  id: number;
  personId: number;
  title: string;
  text: string;
  createdBy: string | null;
  createdAt: Date | null;
  updatedAt: Date | null;
}

/** An interview that names the person. Read-only: interviews are made elsewhere. */
export interface PersonInterview {
  id: number;
  name: string | null;
  date: Date | null;
  status: string | null;
  kind: 'avatar' | 'interview';
  hasTranscription: boolean;
}

export interface VideoFilters {
  /** `null` asks for the videos in no folder; `undefined` does not filter. */
  folderId?: number | null;
  /** Only this person's videos; `null` asks for the videos of nobody; `undefined` does not filter. */
  personId?: number | null;
  /** Only the videos of this group, as its members are now: the Dataset's sidebar. */
  groupId?: number;
  /** The videos that are not in this group: what can still be added to it. */
  notInGroupId?: number;
  /** The same two, for a control: its members live apart from any group's. */
  controlKind?: ControlKind;
  notInControlKind?: ControlKind;
  /** Any of these, OR-ed. */
  statuses?: VideoStatus[];
  /** Any of these, OR-ed. */
  labels?: VideoLabelFilter[];
  configId?: number;
  /** ISO instant, inclusive. */
  createdFrom?: string;
  /** ISO instant, exclusive: the start of the day after the last one asked for. */
  createdTo?: string;
  /** Seconds, inclusive. A video whose duration nobody knows is left out by either bound. */
  durationMin?: number;
  durationMax?: number;
}

export type DeleteVariant = 'keep_analyses' | 'exclude_from_metrics' | 'purge';

export interface AffectedGroup {
  /** A group somebody made, by its id, or a control, by its kind: one of the two, never both. */
  groupId: number | null;
  controlKind: ControlKind | null;
  name: string;
  /**
   * The group has run at least once: its executions are its history, and the analyses they read
   * cannot be erased from under it (`docs/inmutabilidad-de-los-analisis.md`).
   */
  hasExecutions: boolean;
  excludedFromMetrics: boolean;
  snapshotsAffected: number;
}

export interface DeleteVariantOffer {
  variant: DeleteVariant;
  fileDeleted: boolean;
  analysesDeleted: boolean;
  countsInMetrics: boolean;
  /** True when choosing this moves a number that has already been reported. */
  changesPastMetrics: boolean;
  available: boolean;
  unavailableReason: string | null;
}

/**
 * What deleting a video would cost.
 *
 * Read before the dialog opens, not after. «¿Seguro?» does not tell anybody they are
 * about to alter a measurement somebody reported on.
 */
export interface DeleteImpact {
  videoId: number;
  groups: AffectedGroup[];
  /** Some group it is in has run: what turns the purge off. */
  touchesExecutedGroup: boolean;
  runsTotal: number;
  runsCompleted: number;
  labelled: boolean;
  /** Human gesture labels on the video, every scope: lost with it (2026-10-29). */
  gestureLabels: number;
  snapshotsAffected: number;
  variants: DeleteVariantOffer[];
}

/** Where to PUT a file, and the key to register it under afterwards. */
export interface UploadTicket {
  url: string;
  key: string;
  expiresInSeconds: number;
}

/**
 * A video to add to the dataset (product owner, 2026-09-28).
 *
 * No configuration and no engines: registering launches no analysis. Those belong to the group
 * executions, which is where a video is analysed.
 */
export interface VideoUpload {
  videoKey: string;
  filename: string;
  filesizeBytes?: number;
  name?: string;
  description?: string | null;
  folderId?: number | null;
}

export interface RegisteredVideo {
  id: number;
}

/**
 * How to launch a re-analysis: the same options for one video and for many.
 *
 * `analysisConfigId` travels only when the operator chose a different configuration. Sending it
 * rewrites the video's own configuration **before** the run is queued, and it stays rewritten even
 * when the launch is then refused — in a batch, for every video of the selection.
 */
export interface LaunchOptions {
  engines: string[];
  analysisConfigId?: number;
  /** A stored version of the configuration's engines. Absent runs its current one. */
  configVersionId?: string;
  batch?: string;
  note?: string;
  /**
   * Whether the result becomes what the video shows when it succeeds.
   *
   * True also rewrites the video's transcription, and without coherence it rewrites it empty.
   */
  promoteOnSuccess: boolean;
}

/** What launching one run answered. */
export interface LaunchedRun {
  runId: string;
  engines: string[];
  enginesAutoAdded: string[];
  /** Place in the holding pen; null when it went straight to a worker. */
  position: number | null;
}

/**
 * What launching many answered.
 *
 * A refusal on one video never stops the rest, so `rejected` is part of the answer rather than a
 * failure. Its `reason` is Virtual Profiler's own English sentence: a detail, never interface copy.
 */
export interface BulkLaunch {
  batch: string;
  accepted: Array<{ videoId: number; runId: string; position: number | null }>;
  rejected: Array<{ videoId: number; reason: string }>;
}

/** What the re-analysis dialog launched: one video, or a selection under one batch. */
export type ReanalyzeOutcome =
  | { kind: 'single'; videoId: number; run: LaunchedRun }
  | { kind: 'bulk'; videoIds: number[]; launch: BulkLaunch };

/** What replaying a run launched. Same engines, configuration and job as the run it repeats. */
export interface ReplayedRun {
  runId: string;
  replayOf: string;
  position: number | null;
  engines: string[];
}

/** Why an estimate is what it is. `none` means there is no estimate at all. */
export type EstimateBasis = 'same_video' | 'stage_medians' | 'none';

export interface VideoEstimate {
  videoId: string;
  /** Null is «sin datos». Never shown as a zero, which would read as «instant». */
  estimatedMs: number | null;
  basis: EstimateBasis;
  samples: number;
  confident: boolean;
}

export interface BatchEstimate {
  videos: VideoEstimate[];
  /** Serial, deliberately: the dispatcher's worker count moves and a divided total lies. */
  totalMs: number | null;
  /** Videos with no history. A total that leaves some out has to say how many. */
  unknownVideos: number;
  confident: boolean;
}

/**
 * An analysis configuration, as a picker needs it.
 *
 * `version` of null means its engines were never saved: nothing can be analysed with it, and
 * the picker has to say so rather than offering an option that fails at dispatch.
 */
export interface AnalysisConfigSummary {
  id: number;
  name: string;
  description: string | null;
  version: number | null;
  enginesHash: string | null;
  gesturesConfigId: string | null;
  createdAt: Date | null;
  /** Whether an analysis can actually be launched with it. */
  runnable: boolean;
  /**
   * The platform's active configuration: the one Beon analyses with when nobody chose another.
   * At most one is, and the configuration screen opens on it.
   */
  isActive: boolean;
}

/**
 * What activating a configuration did.
 *
 * `previousActiveId` names the configuration whose star just went out, which the screen may be
 * showing. `changed` is false when it already was the active one: nothing was written.
 */
export interface ConfigActivation {
  id: number;
  previousActiveId: number | null;
  changed: boolean;
  activatedAt: Date | null;
}

/**
 * The two engines of a configuration.
 *
 * `Record<string, unknown>` and not a field list: the keys are the engine's and grow with it, and
 * a typed mirror would **drop** the ones it did not know when saving. What the keys mean, their
 * ranges and their Spanish help live in `lib/config-fields.ts`, which is the one table.
 */
export interface AnalysisEngines {
  gestures: Record<string, unknown>;
  coherence: Record<string, unknown>;
}

export interface AnalysisConfigDetail extends AnalysisConfigSummary {
  /** Null when it has never been saved with engines. */
  engines: AnalysisEngines | null;
}

/**
 * What a save produced.
 *
 * `versionCreated` is the field the screen must not skip: saving engines that hash to what the
 * last version already holds stores nothing and answers the old number. «Versión 8 guardada»
 * there is a lie the versions screen contradicts a click later.
 */
export interface SavedConfigVersion {
  gesturesConfigId: string;
  version: number;
  enginesHash: string;
  versionCreated: boolean;
}

export interface SavedConfig {
  id: number;
  version: SavedConfigVersion | null;
}

export type ConfigVersionStatus = 'draft' | 'active' | 'deprecated';

/** One stored version, with what the ledger did with it. */
export interface ConfigVersion {
  id: string;
  version: number;
  createdAt: Date | null;
  createdBy: number | null;
  notes: string;
  enginesHash: string;
  status: ConfigVersionStatus;
  /** Runs that used this exact version. Non-zero is what makes the configuration undeletable. */
  usedByRuns: number;
  /** A run sealed a different hash for this document: it was changed after something ran with it. */
  editedInPlace: boolean;
}

/** The content of one stored version, so an old configuration can be shown and two compared. */
export interface StoredConfigVersion {
  id: string;
  version: number;
  status: ConfigVersionStatus;
  enginesHash: string;
  notes: string;
  createdAt: Date | null;
  createdBy: number | null;
  engines: AnalysisEngines | null;
}

export type CoherenceBindingStatus = 'bound' | 'partial' | 'unbound';

/**
 * One gesture of the engine's catalogue (HU-38).
 *
 * Every field here is read-only. The catalogue is **closed** — it is the engine's registry, and a
 * gesture that is not in it cannot be analysed — so what a configuration chooses is the method,
 * the rules and the switches, never the list.
 */
export interface CatalogueGesture {
  gestureName: string;
  category: string;
  labelEn: string | null;
  labelEs: string | null;
  defaultMethod: 'absolute' | 'baseline';
  defaultApplySustainedRule: boolean;
  defaultDiscardWhileSpeaking: boolean;
  /** False for an input signal: it feeds other gestures and can never raise an alert of its own. */
  canBeGesture: boolean;
  baselineDependent: boolean;
  /**
   * The knobs this gesture takes a value of its own for, in `engines.gestures` spelling.
   *
   * The engine answers it per gesture because the confidence floor only reaches the ones a facial
   * submodel produces, and that list is derived from its detector table. A copy here would be
   * wrong the day the detector gains a class — and wrong in the direction that shows a control
   * doing nothing.
   */
  tunable: string[];
  bindingStatus: CoherenceBindingStatus;
  /** The rules that can score it. Empty with a `bound` status would be a contradiction. */
  ruleIds: string[];
  bindingDetail: string | null;
}

/** What one knob accepts, wherever it is set. The engine's own bounds. */
export interface GestureKnobBounds {
  min: number;
  max: number;
  integer: boolean;
  /** Which gestures it reaches: all, one counting method, or only the facial model. */
  scope: 'all' | 'absolute' | 'baseline' | 'ml' | string;
}

export interface GestureCatalogue {
  gestures: CatalogueGesture[];
  /** What each knob accepts, by its `engines.gestures` key. Empty from an engine too old to say. */
  knobBounds: Record<string, GestureKnobBounds>;
  /**
   * Gesture ids stored by earlier versions, mapped to their current name.
   *
   * Served by the engine, not copied: a configuration saved with an old id whose per-gesture
   * settings are not resolved through this map reverts them to the defaults in silence.
   */
  legacyNames: Record<string, string>;
  /** Which ruleset the bindings are about. «Sin regla» has no meaning without it. */
  rulesetVersion: string;
  /** Rules the ruleset defines that no gesture can reach. */
  unboundRules: string[];
  /** Rules a gesture reaches that the ruleset does not define: drift, made visible. */
  unknownBoundRules: string[];
}

/** One coherence rule (HU-40). `reduction` is the engine's fraction, not a percentage. */
export interface GestureRule {
  ruleId: string;
  ruleLabel: string;
  humanLabel: string | null;
  sourceItem: string;
  sourceValue: string;
  reduction: number;
  windowSeconds: number;
  semanticIntents: string[];
  /** True demands the person be speaking, false demands they not be, null does not care. */
  requiresIsSpeaking: boolean | null;
  baselineDependency: string | null;
}

export interface RulesetVersion {
  version: string;
  label: string;
  /** In canonical order: saving compares against this list, so sorting it breaks the save. */
  rules: GestureRule[];
}

/** What a coherence rule is judged against. */
export type CoherenceRuleOrigin = 'gesture' | 'transcript' | 'cv' | 'dataset' | 'context';

/**
 * A coherence rule, as a configuration stores it (`engines.coherence.coherenceRules`). `reduction`
 * is on a gesture rule's scale: 0.4 subtracts 40 from the idea it fires on.
 */
export interface CoherenceRule {
  ruleId: string;
  label: string;
  description: string;
  origin: CoherenceRuleOrigin;
  reduction: number;
  enabled: boolean;
  /**
   * Coherence 7.3.0, judged rules only (transcript, cv, dataset): what the LLM is told to look
   * for. Required by the engine on every judged rule; absent here is «falta», never the engine's.
   */
  definition?: string;
  /** What an idea is compared against. Required on every judged rule, like the definition. */
  compareWith?: CoherenceCompareWith;
  /** Cases shown to the LLM. Absent or null: none. */
  examples?: CoherenceRuleExample[] | null;
  /** Least confidence a finding needs. Absent or null: the configuration's `semanticMatchThreshold`. */
  minConfidence?: number | null;
}

/**
 * What a judged coherence rule compares an idea against (coherence 7.3.0). `reference` is the
 * CV or the other videos, and is for — and only for — the rules of origin CV and dataset.
 */
export type CoherenceCompareWith =
  'idea' | 'question' | 'overall' | 'transcript' | 'world' | 'reference';

/** A case shown to the LLM: `counts` true is a finding of the rule, false is one that is not. */
export interface CoherenceRuleExample {
  text: string;
  counts: boolean;
}

export interface RulesetCatalogue {
  defaultVersion: string;
  versions: RulesetVersion[];
  /**
   * The engine's own coherence rules, each judged one with its definition and compareWith since
   * 7.3.0: what «Restaurar la del motor» copies. Absent from a catalogue older than them.
   */
  coherenceRules?: CoherenceRule[];
  /** The engine's common prompt of the coherence rules (7.3.0), for «Restaurar la del motor». */
  coherencePrompt?: string;
  /** The defaults and bounds of `referenceLimits` (coherence 8.1.0); absent from an older engine. */
  referenceLimits?: {
    enabled: boolean;
    maxCharsPerReference: number;
    maxOtherVideos: number;
  };
  referenceLimitsBounds?: {
    maxCharsPerReference: { min: number; max: number };
    maxOtherVideos: { min: number; max: number };
  };
}

/**
 * Where a new configuration starts from.
 *
 * `standard` is «Estándar», which the API builds from the engine's catalogues as they are now;
 * `copy` takes another configuration's engines, whole. A configuration is never born empty: the
 * detail screen is where it is changed.
 */
export type ConfigBase = { kind: 'standard' } | { kind: 'copy'; configId: number };

export interface CreateConfigFromBaseInput {
  name: string;
  description: string | null;
  base: ConfigBase;
  /** Overrides the note the API writes on the first version («Estándar: …», «Copia de …»). */
  note?: string;
}

/**
 * One execution — a batch over a group — run with a configuration, and how it did.
 *
 * `methods` are the three product metrics per score method, as Virtual Profiler froze them in the
 * batch's automatic measurement; this side never recomputes them. Null while the batch runs, when
 * it was cancelled, or when it was never measured. A null figure inside is «sin datos», not zero.
 */
export interface ConfigExecution {
  /** Whose execution it is: a group, by its id, or a control, by its kind. Never both. */
  groupId: number | null;
  controlKind: ControlKind | null;
  /** The group's name; a control's is its title (`CONTROL_KIND_TITLES`). */
  groupName: string;
  batch: string;
  startedAt: Date | null;
  finishedAt: Date | null;
  complete: boolean;
  cancelled: boolean;
  /**
   * Where it stands in its group's queue of executions, as the group's history says: `paused` is
   * unfinished and not running. Null (or absent) from an API that does not send it.
   */
  queueState?: GroupExecutionQueueState | null;
  configVersion: number | null;
  /** The idea threshold the figures were scored at: two thresholds are two different rulers. */
  thresholdPct: number | null;
  snapshotId: string | null;
  videosTotal: number | null;
  videosEvaluated: number | null;
  methods: Record<string, MethodDelta> | null;
  /** The engines it was launched with; empty when nobody can say (an older execution). */
  engines?: string[];
}

// ---------------------------------------------------------------------------
// Analysis groups (HU-19, HU-24 to HU-32)
// ---------------------------------------------------------------------------

export type GroupSourceType = 'interview' | 'manual_analysis';

/** The headline three of a measurement, as a snapshot recorded them. */
export interface GroupMeasurement {
  snapshotId: string;
  computedAt: Date;
  incoherencesDetectedPct: number | null;
  falsePositivesPct: number | null;
  correctPct: number | null;
  /** «Métricas globales»; absent from an API older than them. */
  incoherencesDetectedAllVideosPct?: number | null;
  falsePositivesAllVideosPct?: number | null;
  /**
   * Every method's figures. The flat ones above are the sum's (`FLAT_FIELDS_METHOD`), so a screen
   * reads the user's preferred method from here (`preferredFigure`), never from them. From an API
   * older than it, only the sum is in it.
   */
  methods?: Record<string, MethodDelta>;
}

/** A configuration as an execution names it. */
export interface ExecutionConfigRef {
  id: number | null;
  name: string | null;
  version: number | null;
}

/**
 * One thing that differs between two executions of a group, or between its latest execution and
 * what running it today would run.
 *
 * A code and its values, never prose: `lib/group-history.ts` writes the sentence. A null threshold
 * is «the configuration's own». Between two executions any of them can appear, since each launch
 * chooses its own configuration, threshold and engines. Against today only three can
 * (`GroupReanalysis`): the configuration that execution used got a newer version, an engine it ran
 * was updated — the changes nobody makes on the group — or its videos changed after it started
 * (`members_changed`, product owner 2026-10-06), so its figures are no longer the group's.
 */
export type ExecutionChange =
  | { code: 'config_changed'; from: ExecutionConfigRef; to: ExecutionConfigRef }
  | {
      code: 'config_version_changed';
      configId: number | null;
      from: number | null;
      to: number | null;
    }
  | { code: 'threshold_changed'; from: number | null; to: number | null }
  | { code: 'engines_changed'; from: string[]; to: string[] }
  | { code: 'engine_version_changed'; engine: string; from: string; to: string }
  | { code: 'members_changed'; changedAt: Date | null };

/**
 * - `never_run`: no execution to compare with. «Sin ejecuciones».
 * - `running`: one is in flight. «En marcha».
 * - `up_to_date`: its latest execution's configuration, engines and videos are still the current
 *   ones. «Al día».
 * - `outdated`: they are not — `reasons` says what moved. «Desactualizado».
 */
export type ReanalysisStatus = 'never_run' | 'running' | 'up_to_date' | 'outdated';

/**
 * Whether the group's latest execution is still current, as Virtual Profiler worked it out.
 *
 * Compared with its latest execution that was not cancelled, for three things only: the
 * configuration that execution used has a newer version now (`config_version_changed`), the engine
 * versions deployed **now** differ for an engine it ran (`engine_version_changed`), or videos were
 * added to the group or taken out of it after that execution started (`members_changed`). The
 * threshold and the engine selection are not reasons: each launch chooses them.
 */
export interface GroupReanalysis {
  status: ReanalysisStatus;
  /** The execution it was compared with; null when there is none. */
  latestBatch: string | null;
  reasons: ExecutionChange[];
  /**
   * False when the deployed engine versions could not be read: a new engine may be waiting and the
   * answer cannot say so.
   */
  engineVersionsChecked: boolean;
}

/**
 * What the latest execution of a group that was not cancelled ran with, as its launch recorded it.
 *
 * `config` of null: nothing is known of it. `thresholdPct` of null is «the configuration's own» —
 * or unknown, for an execution older than the launch records, whose engines are empty too.
 */
export interface GroupLatestParameters {
  batch: string;
  config: ExecutionConfigRef | null;
  thresholdPct: number | null;
  engines: string[];
}

/**
 * The four things the product is controlled on (product owner, 2026-10-06; static and apart from
 * the groups since 2026-10-28): the gesture detection alone (`gestures`), the alerts a person
 * labelled against the engine's alerts (`gesture_alerts`, its habituality rules), the coherence
 * method fed the alerts a person labelled (`semantics`, 2026-10-29), and both as a real analysis
 * runs them (`coherence`). A control is not a row of the groups: it lives in code, is never created, renamed
 * or deleted, and its members, executions and measurements are kept by its kind.
 */
export type ControlKind = 'gestures' | 'gesture_alerts' | 'semantics' | 'coherence';

/**
 * Who owns a per-group resource — members, executions, measurements, metrics: a group somebody
 * made, by its id, or a control, by its kind. The generic hooks, apis and query keys take one, so a
 * control's page reads the same things a group's does without an id (`lib/group-owner.ts`).
 */
export type GroupOwner = { groupId: number } | { controlKind: ControlKind };

/**
 * A group, as the screens read it: a set of videos somebody made (product owner, 2026-09-26). The
 * controls are not among them (2026-10-28): see `ControlGroup`.
 *
 * It holds no configuration, threshold nor engines: each execution chooses them when it is launched
 * (`GroupLaunchParameters`) and records them. `latestParameters` is what its latest execution ran
 * with, null when it never ran; `reanalysis` says whether that execution is still current, null
 * from a Virtual Profiler older than it.
 */
export interface AnalysisGroup {
  id: number;
  name: string;
  description: string | null;
  /** When a video last entered or left it; null when that never happened or is not known. */
  membersChangedAt: Date | null;
  latestParameters: GroupLatestParameters | null;
  reanalysis: GroupReanalysis | null;
  createdAt: Date | null;
  updatedAt: Date | null;
  members: number;
  runs: number;
  runsByStatus: Partial<Record<RunStatus, number>>;
  /**
   * Executions launched while the group was busy, waiting their turn: they have no runs yet, so
   * `runsByStatus` does not see them. 0 from a Virtual Profiler older than the queue.
   */
  executionsWaiting: number;
  lastBatch: string | null;
  /** Null means the group has measured nothing, which is not «0 %». */
  lastMeasurement: GroupMeasurement | null;
}

/**
 * Which run a member is evaluated by, and why.
 *
 * `bucket` is the field that decides whether the member counts. A screen showing only the
 * percentage would hide that a third of the group never ran.
 */
export interface MemberResolution {
  bucket: string;
  runId: string | null;
  status: RunStatus | null;
  degraded: boolean;
  notRequested: boolean;
  engineVersions: Record<string, string> | null;
  enginesHash: string | null;
}

export interface GroupMember {
  memberId: number;
  /** Whose member it is: a group, by its id, or a control, by its kind. Never both. */
  groupId: number | null;
  controlKind: ControlKind | null;
  sourceType: GroupSourceType;
  sourceId: number;
  name: string | null;
  sourceStatus: string | null;
  label: VideoLabel | null;
  /** A pinned run is frozen for this member: a later successful run will not replace it. */
  pinnedRunId: string | null;
  addedAt: Date | null;
  resolved: MemberResolution;
}

/** What adding videos to a group did. */
export interface AddedMembers {
  /** Every video asked for that is a member now — the ones that already were, too. */
  items: GroupMember[];
  /** How many are new: the count to report, since `items` includes the old members. */
  added: number;
  /** How many of the ones asked for do not exist any more. */
  missing: number;
}

export interface GroupFilters {
  search?: string;
}

/** A group is its videos: a name, what it is for, and the videos to create it with. */
export interface CreateGroupDraft {
  name: string;
  description?: string | null;
  /** Videos to create it with, so the group is never half-made. */
  videoIds?: number[];
}

export interface UpdateGroupDraft {
  name?: string;
  description?: string | null;
}

/**
 * Where a run's gestures come from: the gestures engine (every run until the control groups), or
 * the gestures a person labelled on the video, which coherence then reads instead of running the
 * engine (product owner, 2026-09-28).
 */
export type GestureSource = 'engine' | 'human_labels';

/**
 * What one execution of a group is launched with, chosen at each launch.
 *
 * `configVersionId` absent runs the configuration's current version. `thresholdPct` null is the
 * configuration's own idea threshold. `engines` is what was asked for: upstream closes it over the
 * same rules as `lib/engine-rules.ts` (coherence brings gestures). `gestureSource` absent is the
 * engine; `human_labels` keeps both engines in `engines` and tells the worker to take the gestures
 * from the labels instead of running the gestures engine.
 */
export interface GroupLaunchParameters {
  analysisConfigId: number;
  configVersionId?: string;
  engines: string[];
  thresholdPct: number | null;
  gestureSource?: GestureSource;
}

/**
 * Why a member was left out of a group launch, in Virtual Profiler's own words.
 *
 * `already_active`, `engines_not_selectable`, `source_not_found` or `launch_failed` — and
 * `not_a_member` when a launch names videos. A launch fed by human labels leaves out a video whose
 * alerts labelling is not marked complete or has no alerts (`alert_labels_missing`, 2026-10-29);
 * executions stored before that date carry `gesture_labels_missing` and
 * `gesture_labelling_incomplete`, from the gestures labelling they read. A launch with coherence
 * leaves out a video whose transcription is not done (`transcription_not_ready`, the detail says its
 * status). A control run leaves out its pending videos by their first missing piece, so also
 * `truth_label_missing` and `alert_labels_missing` (Alertas gestuales and Semántica), with all of
 * them in the detail. A pinned member is **not** skipped: pinning decides which run a member is evaluated
 * by, not whether it runs.
 */
export type GroupSkipReason =
  | 'already_active'
  | 'engines_not_selectable'
  | 'source_not_found'
  | 'launch_failed'
  | 'not_a_member'
  | 'gesture_labels_missing'
  | 'gesture_labelling_incomplete'
  | 'truth_label_missing'
  | 'alert_labels_missing'
  | 'transcription_not_ready';

/**
 * What a group launch answered.
 *
 * `skipped` matters as much as `accepted`: a screen counting only the accepted runs would report
 * «4 lanzados» for a group of five, and send somebody looking for the fifth.
 */
/**
 * How the email about an execution stands: waiting for it to finish (or for a retry), `sent`,
 * `failed` for good, or `dropped` because the execution will never finish — cancelled.
 */
export type ExecutionEmailStatus = 'pending' | 'sent' | 'failed' | 'dropped';

/**
 * Being emailed when an execution finishes. Virtual Profiler sends it, to the session's own
 * address — which is why the address is read back here and never sent.
 */
export interface ExecutionSubscription {
  /** Whose execution it is: a group, by its id, or a control, by its kind. Never both. */
  groupId: number | null;
  controlKind: ControlKind | null;
  batch: string;
  email: string;
  /** Kept as a string: a status upstream adds tomorrow must not read as one of these. */
  status: ExecutionEmailStatus | string;
  sentAt: Date | null;
}

export interface GroupRunResult {
  batch: string;
  accepted: Array<{
    memberId: number;
    sourceId: number;
    runId: string;
    position: number | null;
  }>;
  skipped: Array<{
    memberId: number | null;
    sourceId: number;
    /** Kept as a string: a reason upstream adds tomorrow must still reach the screen. */
    reason: GroupSkipReason | string;
    /** Upstream's sentence about it, when it sent one. A detail, never interface copy. */
    detail: string | null;
  }>;
  /**
   * The group was busy, so the execution waits its turn: it starts when the one in flight has
   * finished, and `accepted` and `skipped` are empty until then. False from an older Virtual
   * Profiler.
   */
  queued: boolean;
  /** Its place among the group's waiting executions, 1 = next; null when it started now. */
  queuePosition: number | null;
}

// ---------------------------------------------------------------------------
// Control groups (product owner, 2026-09-28; static since 2026-10-06; apart from the groups since
// 2026-10-28)
// ---------------------------------------------------------------------------

/** What upstream counted over the control group, for the checks and warnings. */
export interface ControlChecks {
  /** Members counting towards metrics. */
  members: number;
  /** Members that are videos: only a video can be labelled. */
  manualMembers: number;
  withTruthLabel: number;
  withoutTruthLabel: number;
  /** Videos with at least one gesture label. */
  withGestureLabels: number;
  /** Videos whose labelling is marked complete. */
  gestureLabellingComplete: number;
  /** Labels over the whole group. */
  gestureLabels: number;
  /** Videos whose transcription is done: coherence reads it, and skips a video without it. */
  withTranscription: number;
}

/** The latest finished, not cancelled, execution of the group that fits its kind. */
export interface ControlExecution {
  batch: string;
  finishedAt: Date | null;
  engines: string[];
  /** Kept as a string: a source upstream adds tomorrow must not read as one of these. */
  gestureSource: GestureSource | string;
  engineVersions: Record<string, string> | null;
  thresholdPct: number | null;
}

/**
 * How well the engine detects the gestures a person labelled, computed upstream from the fitting
 * execution's episodes and the videos whose labelling is complete. Percentages, null when their
 * denominator is 0.
 *
 * Alertas gestuales has the same figures over other inputs (product owner, 2026-10-29): the labels
 * of scope `gesture_alerts` against the engine's alert episodes only, after its habituality rules.
 * The shape is the same, so it is read and shown with the same builders; only the words change.
 */
export interface GestureControlMetrics {
  kind: 'gestures';
  detectionPct: number | null;
  falsePositivePct: number | null;
  correctVideosPct: number | null;
  labels: number;
  labelsDetected: number;
  detections: number;
  falsePositives: number;
  videosEvaluated: number;
}

/**
 * The fitting execution's measurement headlines, as a batch has them. The flat ones are `method`'s
 * («Alerta mayor»); `methods` has every method, and the screens read the user's preferred one from
 * there (`preferredFigure`). From an API older than `methods`, only the flat method is in it.
 */
export interface CoherenceControlMetrics {
  kind: 'coherence';
  method: string;
  incoherencesDetectedPct: number | null;
  falsePositivesPct: number | null;
  correctPct: number | null;
  incoherencesDetectedAllVideosPct: number | null;
  falsePositivesAllVideosPct: number | null;
  methods?: Record<string, MethodDelta>;
}

/**
 * Veracidad's «Métricas de alertas» (product owner, 2026-10-20), computed upstream from the same
 * execution as its figures: the gestures engine's alerts of each labelled video's run against its
 * lie ranges, per video — never the «global» reading. Percentages carry the names of the Veracidad
 * figures and are null when their base is 0; the counts are their bases and the note's.
 */
export interface CoherenceAlertMetrics {
  kind: 'coherence';
  /** Of the Mentira videos, those with an alert overlapping one of their ranges. */
  incoherencesDetectedPct: number | null;
  /** Of the Verdad videos, those with any alert. */
  falsePositivesPct: number | null;
  /** Of the labelled videos, the Mentira ones detected and the Verdad ones without alerts. */
  correctPct: number | null;
  videosEvaluated: number;
  videosWithIncoherence: number;
  incoherentsDetected: number;
  videosTruthful: number;
  truthfulFlagged: number;
  correctVideos: number;
  alerts: number;
  /** Alerts of Mentira videos overlapping one of their ranges. */
  alertsInRanges: number;
  /** Every other alert: those of Verdad videos and those of Mentira videos outside their ranges. */
  alertsOutsideRanges: number;
}

/**
 * One control group: its kind's title and what it is for, its videos and executions as a group
 * carries them (the same fields, so what reads a group's busyness or staleness reads a control's),
 * what upstream checked about it, what is wrong with it (codes, worded on screen), the execution
 * its figures come from and the figures — detection for `gestures`, the batch headlines for the
 * other two; null when no execution fits.
 *
 * `ready` is the kind's business rule, upstream's word: at least one video, and none of them
 * `pending` — lacking something the kind measures with (`ControlPendingVideo`) — so its figures
 * speak for the whole group. A run does not wait for it: it leaves the pending videos out.
 */
export interface ControlGroup {
  kind: ControlKind;
  /** Its kind's title, upstream's word; the screens say `CONTROL_KIND_TITLES[kind]`. */
  name: string;
  /** The one thing of a control somebody can write. */
  description: string | null;
  /** How many videos it holds. */
  members: number;
  /** When a video last entered or left it; null when that never happened or is not known. */
  membersChangedAt: Date | null;
  /** As on a group: whether its latest execution is still current; null from an older upstream. */
  reanalysis: GroupReanalysis | null;
  runsByStatus: Partial<Record<RunStatus, number>>;
  /** Executions launched while it was busy, waiting their turn: they have no runs yet. */
  executionsWaiting: number;
  /** What its latest execution ran with, null when it never ran. */
  latestParameters: GroupLatestParameters | null;
  checks: ControlChecks;
  /** Codes, kept as strings: a warning upstream adds tomorrow still reaches the screen. */
  warnings: string[];
  latestExecution: ControlExecution | null;
  metrics: GestureControlMetrics | CoherenceControlMetrics | null;
  /**
   * Veracidad's alert figures (product owner, 2026-10-20): the gestures engine's alerts against the
   * lie ranges, per video, computed upstream from the same execution. Null on every other kind —
   * Gestos lost its alert set once the alerts got a control of their own (2026-10-29) —, with no
   * execution, or from a Virtual Profiler older than them.
   */
  alertMetrics?: CoherenceAlertMetrics | null;
  ready: boolean;
  pending: number;
}

/**
 * What a video of a control group may lack. Which ones a kind asks for is upstream's rule: gestures
 * the labels and their completion; gesture alerts its alert labelling marked complete (a complete
 * document with no labels is a video without alerts, and ready); semantics the alert labelling —
 * complete and with at least one alert, since the veracity engine is fed them (2026-10-29) — and
 * the truth label; coherence the truth label and the transcription.
 */
export type ControlMissing =
  | 'gesture_labels'
  | 'gesture_labelling_complete'
  | 'alert_labels'
  | 'truth_label'
  | 'transcription';

/** A video of a control group that is not ready for its kind, with what it lacks. */
export interface ControlPendingVideo {
  sourceType: GroupSourceType;
  sourceId: number;
  name: string;
  /** Kept as strings: a requirement upstream adds tomorrow still reaches the screen. */
  missing: Array<ControlMissing | string>;
}

/**
 * The kinds whose figures are read against the gestures a person labelled, and so can be opened
 * video by video and gesture by gesture. Coherencia is measured against truth or lie only: it has
 * no gesture map.
 */
export type GestureMatrixKind = Extract<ControlKind, 'gestures' | 'semantics'>;

/**
 * Why a video of the group is a row of the gesture map and not part of its figures, in the order
 * upstream checks: it has no result in the execution, its labelling is not marked complete, or that
 * run did not execute the detector — every run of Semántica, which reads the labels instead, and any
 * run that skipped it. A video with neither a result nor its labelling finished says `no_result`.
 */
export type GestureMatrixNotEvaluated =
  'labelling_incomplete' | 'no_result' | 'gestures_not_executed';

/**
 * One gesture of one video: its labels, how many a detection matched, the detections and how many
 * matched no label. A detection only matches a label of its own gesture, so a video's cells add up
 * to its figures.
 */
export interface GestureMatrixCell {
  gesture: string;
  labels: number;
  labelsDetected: number;
  detections: number;
  falsePositives: number;
}

/**
 * One video of the group, evaluated or not: every video is a row, so the map also says which ones
 * the figures leave out and why. The figures and `correct` are null on a video left out, and its
 * `cells` empty; on one evaluated, `cells` holds only the gestures with something in them, by
 * gesture id and not in the columns' order — a cell is looked up by its gesture.
 */
export interface GestureMatrixVideo {
  sourceType: 'manual_analysis';
  sourceId: number;
  /** Null when upstream no longer finds the video to name it. */
  name: string | null;
  evaluated: boolean;
  /** Kept as a string: a reason upstream adds tomorrow still reaches the screen. */
  notEvaluatedReason: GestureMatrixNotEvaluated | string | null;
  labels: number | null;
  labelsDetected: number | null;
  detections: number | null;
  falsePositives: number | null;
  /** The gestures control's rule for one video: ≥ 80 % of its labels detected, ≤ 20 % extra. */
  correct: boolean | null;
  cells: GestureMatrixCell[];
  /**
   * The video was deleted from the dataset after the execution closed. Its figures are part of
   * what the execution measured and stay; the row is struck through (2026-10-29).
   */
  sourceDeleted: boolean;
}

/**
 * A control group's gestures figures of one execution, opened by video and gesture, so a
 * percentage on its card can be traced to what makes it. `batch` null: the group has no execution
 * that fits, and everything is empty. `totals` are exactly that execution's gestures figures.
 */
export interface GestureMatrix {
  kind: GestureMatrixKind;
  batch: string | null;
  /** The columns: the gestures of the evaluated cells, the most labelled first, then by name. */
  gestures: string[];
  /** Sorted by name, then id. */
  videos: GestureMatrixVideo[];
  totals: GestureControlMetrics;
}

/**
 * What a control group's execution is launched with. No engines and no gesture source: upstream
 * takes both from the kind, so an execution of a control group always fits it.
 */
export type ControlLaunchParameters = Pick<
  GroupLaunchParameters,
  'analysisConfigId' | 'configVersionId' | 'thresholdPct'
>;

/**
 * An execution launched while its group was busy, waiting its turn (product owner, 2026-09-26).
 *
 * It has no runs, no metrics and no members by batch yet: it is not in the history (`GroupBatch`)
 * and never «the latest execution». `starting` is the moment its runs are being created.
 */
export interface GroupWaitingExecution {
  batch: string;
  state: 'waiting' | 'starting';
  /** 1 = the next one to start. */
  queuePosition: number;
  /** When it was launched: the button, not when it will start. */
  queuedAt: Date;
  /** Virtual Profiler's user id of whoever launched it; null when unknown. */
  launchedBy: number | null;
  /** What it will run with, as its launch recorded it; null when unknown. */
  config: ExecutionConfig | null;
  /** Null: each configuration's own. */
  thresholdPct: number | null;
  /** The configuration version's own threshold, in numbers; null (or absent) when unknown. */
  configThresholdPct?: number | null;
  engines: string[];
  /** Where coherence will take its gestures from; absent (an older API) is `engine`. */
  gestureSource?: GestureSource | string;
  /** How many members were chosen by hand; null = the whole group, as it is when it starts. */
  members: number | null;
}

/** A group's executions: the history, and the ones waiting their turn (next first). */
export interface GroupExecutionList {
  items: GroupBatch[];
  waiting: GroupWaitingExecution[];
}

/**
 * One execution of a group, as the history lists it.
 *
 * `runs` and `runsByStatus` count every attempt, automatic retries included: a video that failed
 * and then succeeded on its retry is one FAILED and one SUCCEEDED. They are not a count of videos.
 * `members` and `membersByStatus` are: each video once, by its latest run there — the way the
 * metrics of that execution count it. Null from an API that does not send them.
 */
export interface GroupBatch {
  batch: string;
  startedAt: Date | null;
  /** Null while the batch is still running, which the history has to show. */
  finishedAt: Date | null;
  runs: number;
  runsByStatus: Partial<Record<RunStatus, number>>;
  members: number | null;
  membersByStatus: Partial<Record<RunStatus, number>> | null;
  /**
   * Every run of it reached a final state, automatic retries included.
   *
   * What decides whether its numbers exist: an execution still retrying a video has not finished
   * measuring it.
   */
  complete: boolean;
  snapshotId: string | null;
  /**
   * Somebody cancelled it, or took it out of the queue, as a whole.
   *
   * It stays in the history, marked as such, but it is not a measurement: upstream writes no
   * automatic snapshot for it and the screens that read «the latest execution» skip it. False from
   * an API older than the cancellation of executions.
   */
  cancelled: boolean;
  /** The configuration its runs were sealed with; null when unknown. */
  config: ExecutionConfig | null;
  /**
   * The threshold chosen when it was launched (null: the configuration's own). For an execution
   * older than the launch record, the threshold its measurement was scored at, or null.
   */
  thresholdPct: number | null;
  /**
   * What «the configuration's own» is in numbers: the threshold of the configuration version it
   * ran with. Null (or absent) when upstream does not say.
   */
  configThresholdPct?: number | null;
  /** The engines it was launched with; empty when unknown. */
  engines: string[];
  /**
   * Where coherence took its gestures from. `human_labels`: the gestures engine did not run, even
   * with `gestures` among `engines`. Absent (an older API) is `engine`.
   */
  gestureSource?: GestureSource | string;
  /** The engine versions its runs were sealed with; null until one has been. */
  engineVersions: Record<string, string> | null;
  /** Its measurement's series identity: two executions with the same one measured alike. */
  seriesKey: string | null;
  /**
   * `recorded` when the launch wrote its parameters down, `runs` when they were read back from an
   * older execution. Null from an API older than the history.
   */
  parametersSource: 'recorded' | 'runs' | null;
  /** What it scored, frozen with its own threshold. Null while it runs, and when cancelled. */
  metrics: ExecutionMeasurement | null;
  /**
   * Where it stands in its group's queue of executions, upstream's word: `paused` once somebody
   * paused it, `waiting` once resumed and waiting its turn again. Null (or absent) from an API that
   * does not send it.
   */
  queueState?: GroupExecutionQueueState | null;
  /**
   * The videos it ran, as a version of the group's dataset. Null (or absent) from an API older
   * than it.
   */
  dataset?: ExecutionDataset | null;
}

/** A video that came into, or went out of, a group's dataset between two executions. */
export interface ExecutionDatasetSource {
  sourceType: GroupSourceType;
  sourceId: number;
  /** Null when it could not be named — a video deleted since, for one. */
  name: string | null;
}

/**
 * The set of videos one execution ran, as a version of its group's dataset (product owner,
 * 2026-10-12). Numbered by the order each set first ran in the group: an execution over exactly the
 * videos of an earlier one is that version again, and its figures are comparable with it again.
 */
export interface ExecutionDataset {
  /** 1 is the first set of videos the group ran. */
  version: number;
  /** Equal keys are the same set of videos. */
  key: string;
  videos: number;
  /** The version of the execution launched before it; null for the group's first. */
  previousVersion: number | null;
  /** Against the execution launched before it; both empty when the videos did not change. */
  added: ExecutionDatasetSource[];
  removed: ExecutionDatasetSource[];
}

/**
 * What a stored dataset version pins of each video (product owner, 2026-10-29): the human gesture
 * labels, the human alert labels, or the truth/lie labelling. Which ones is the owner's: a control
 * pins what its kind measures with, a group somebody made the truth.
 */
export type DatasetLabelType = 'gestures' | 'gesture_alerts' | 'truth';

/**
 * Why a version was stored: the videos changed, their labels did, somebody asked, an execution was
 * launched over a set not stored yet, or the migration numbered the sets the runs had made.
 */
export type DatasetVersionReason = 'members' | 'labels' | 'manual' | 'execution' | 'migration';

/**
 * One stored version of an owner's dataset (product owner, 2026-10-29): its videos and their labels
 * at one moment, numbered as the executions' datasets are — «v3» in the history is «v3» here.
 * Nothing changes nor deletes one; they go with their owner.
 */
export interface DatasetVersionSummary {
  version: number;
  createdAt: Date;
  /** Who asked for it; null when a hook, a launch or the migration did. */
  createdBy: string | null;
  reason: DatasetVersionReason;
  note: string | null;
  videos: number;
  /** How many of them were fully labelled for the owner's label types. */
  labelled: number;
  previousVersion: number | null;
  /** Against the version before it; both empty for the first. */
  added: ExecutionDatasetSource[];
  removed: ExecutionDatasetSource[];
  labelTypes: DatasetLabelType[];
}

/** A human gesture or alert labelling as a version pinned it. */
export interface DatasetVersionGestureLabels {
  complete: boolean;
  count: number;
  hash: string;
  version: number;
}

/** A truth/lie labelling as a version pinned it. */
export interface DatasetVersionTruthLabels {
  state: 'incoherent' | 'coherent' | 'unlabelled';
  complete: boolean;
  /** The lie's tramos. */
  ranges: number;
  hash: string;
}

export interface DatasetVersionMember {
  sourceType: GroupSourceType;
  sourceId: number;
  name: string | null;
  /** The video is deleted now, whatever it was when the version was stored. */
  sourceDeleted: boolean;
  /** Only the owner's label types; null for a version the migration made, whose labels nobody recorded. */
  labels: {
    gestures?: DatasetVersionGestureLabels;
    gestureAlerts?: DatasetVersionGestureLabels;
    truth?: DatasetVersionTruthLabels;
  } | null;
}

export interface DatasetVersionDetail extends DatasetVersionSummary {
  /** Equal keys are the same videos with the same labels. */
  key: string;
  members: DatasetVersionMember[];
}

/** Where the owner's dataset stands now, against its stored versions. */
export interface DatasetCurrent {
  key: string;
  /** The latest stored version when the dataset is it; null when it moved since. */
  version: number | null;
  /** Not the latest stored version — true with none stored and some videos. */
  changed: boolean;
  /** Videos not fully labelled for the owner's label types. */
  pending: number;
}

export interface DatasetVersionList {
  /** Newest first. */
  items: DatasetVersionSummary[];
  current: DatasetCurrent;
}

/**
 * - `waiting` / `starting`: its turn has not come, or its runs are being queued right now;
 * - `started`: its runs were queued;
 * - `paused`: somebody paused it, and its runs that had not started wait (`PAUSED`).
 */
export type GroupExecutionQueueState = 'waiting' | 'starting' | 'started' | 'paused';

/** The configuration an execution ran with, as its history names it. */
export interface ExecutionConfig {
  id: number | null;
  /** Its name when the execution was launched; null for one read back from its runs. */
  name: string | null;
  version: number | null;
  /** The stored version the runs were sealed with. */
  versionId: string | null;
  /** Content hash of that version: two versions measure alike when it matches. */
  enginesHash: string | null;
}

/** The automatic measurement an execution left when it completed. */
export interface ExecutionMeasurement {
  snapshotId: string;
  /** The threshold it was scored at: the execution's own, never today's. */
  thresholdPct: number;
  seriesKey: string | null;
  comparableWithPrevious: boolean;
  videosTotal: number | null;
  videosEvaluated: number | null;
  /** The three product metrics per score method, by name. A null figure is «sin datos». */
  methods: Record<string, MethodDelta>;
}

/**
 * What cancelling one execution of a group did to each of its runs, by run id.
 *
 * - `withdrawn`: it had not started and left the queue;
 * - `cancelling`: it was running and was asked to stop at its next checkpoint;
 * - `finishing`: it was already saving its result, too late to stop, and will finish;
 * - `untouched`: it had finished, or was already stopping. A finished run is never cancelled.
 *
 * What the stopped runs had written is discarded upstream; what had finished is kept.
 */
export interface GroupBatchCancellation {
  batch: string;
  withdrawn: string[];
  cancelling: string[];
  finishing: string[];
  untouched: string[];
  /**
   * It was waiting its turn and left the queue: nothing of it ran, so it leaves no trace in the
   * history. False from an older Virtual Profiler.
   */
  dequeued: boolean;
}

/**
 * What pausing one execution did: `paused` runs had not started and now wait; `stillRunning` ones
 * had, and finish.
 */
export interface GroupBatchPause {
  batch: string;
  paused: number;
  stillRunning: number;
}

/**
 * What resuming one execution did: `started` when its runs went back to the queue at once — the
 * group was idle — and `waiting` when it waits its turn behind the group's execution in flight.
 */
export interface GroupBatchResume {
  batch: string;
  state: 'waiting' | 'started';
}

/** What deleting one execution did: its runs are gone, with their results and its figures. */
export interface GroupBatchDeletion {
  batch: string;
  deletedRuns: number;
}

/**
 * A stored measurement of a group.
 *
 * Immutable and append-only. `comparableWithPrevious` is what says whether the point beside it
 * means the same thing — a chart that ignored it would draw a change of instrument as an
 * improvement.
 */
export interface MetricsSnapshot {
  snapshotId: string;
  /** Whose measurement it is: a group, by its id, or a control, by its kind. Never both. */
  groupId: number | null;
  controlKind: ControlKind | null;
  batch: string | null;
  /**
   * Why this point exists.
   *
   * `auto` is an execution that finished, `manual` somebody pressing «guardar medición», and
   * `answer_key` a point nobody asked for: the group's numbers moved because what it is measured
   * *against* changed — a truth label corrected, a video joining or leaving — with no execution to
   * attribute it to. It measures the whole group, so it carries no batch.
   */
  trigger: 'auto' | 'manual' | 'answer_key';
  computedAt: Date;
  thresholdPct: number;
  /** The default method's headlines, which is what a series draws. */
  incoherencesDetectedPct: number | null;
  falsePositivesPct: number | null;
  correctPct: number | null;
  /** Every score method, by name. Empty from an API that does not send them. */
  methods: Record<string, MethodDelta>;
  /** The analysis configuration version the measured runs were sealed with. */
  configVersion: number | null;
  videosEvaluated: number;
  videosTotal: number;
  seriesKey: string | null;
  comparableWithPrevious: boolean;
  note: string | null;
}

/**
 * Where a video's groups come from in a gallery: one call for the whole page. A control it belongs
 * to is listed too, by its kind and with no id.
 */
export interface GroupMembershipRef {
  groupId: number | null;
  controlKind: ControlKind | null;
  name: string;
  pinnedRunId: string | null;
}

export interface MethodDelta {
  incoherencesDetectedPct: number | null;
  falsePositivesPct: number | null;
  correctPct: number | null;
  /**
   * The «Métricas globales» (product owner, 2026-09-27), every figure over the whole dataset: lie
   * videos detected, of every video; and videos with a flagged idea wholly outside any lie window,
   * lie videos included. Absent where upstream does not send them.
   */
  incoherencesDetectedAllVideosPct?: number | null;
  falsePositivesAllVideosPct?: number | null;
}

/**
 * A comparison of two measurements.
 *
 * `sameSeries` is the field that decides whether the deltas may be presented as progress. When
 * it is false, two things changed at once and the numbers are not an improvement — they are a
 * different measurement.
 */
/** How one video came out in one execution under one method. */
export interface VideoOutcome {
  /** `incoherent_detected`, `incoherent_missed`, `false_positive` or `truthful_cleared`. */
  outcome: string;
  /** A flagged idea wholly outside any lie window: a false alarm under the global metrics. */
  falsePositive: boolean;
}

/** A video both executions measured that came out differently in them under some method. */
export interface VideoFlip {
  sourceId: number;
  name: string | null;
  label: VideoLabel | null;
  methods: Record<string, { a: VideoOutcome | null; b: VideoOutcome | null }>;
}

export interface GroupComparison {
  deltas: Record<string, MethodDelta>;
  /** Each side's figures per method, in both readings. Empty from an older API. */
  figures: Record<string, { a: MethodDelta; b: MethodDelta }>;
  /** The common videos whose outcome changed. Empty from an older API. */
  flips: VideoFlip[];
  sameSeries: boolean;
  commonOnly: boolean;
  onlyInA: Array<{ sourceId: number; name: string | null }>;
  onlyInB: Array<{ sourceId: number; name: string | null }>;
  common: number;
  /**
   * Between two executions of one group: which parameters changed from A to B, the thresholds
   * being the ones each was scored at. Null between groups, and from an older API.
   */
  parameterChanges: ExecutionChange[] | null;
}

/**
 * Group metrics, flattened to what the screens read.
 *
 * Read for one execution (`batch`), they count each member by its latest run there and nothing
 * else: a member whose run failed or was cancelled is left out of every denominator rather than
 * scored as a miss, and one the execution did not include is not in them.
 */
export interface GroupMetrics {
  /** Whose figures they are: a group, by its id, or a control, by its kind. Never both. */
  groupId: number | null;
  controlKind: ControlKind | null;
  /**
   * The execution the numbers are about, as upstream says it answered. Null for the older reading
   * across executions — which a screen asking for one execution must not show as that one's.
   */
  batch: string | null;
  seriesKey: string | null;
  thresholdPct: number;
  videosTotal: number;
  videosLabeled: number;
  videosEvaluated: number;
  methods: Record<string, MethodDelta>;
}

/**
 * The coherence metrics, as the dashboard reads them.
 *
 * `evaluated` below `labelled` is the fact the screen exists to show: a labelled video that was
 * never analysed stays in every denominator, so a metric cannot be improved by analysing less.
 * Hiding the gap would make the false-alarm target reachable by doing nothing.
 */
export interface CoherenceMetrics {
  scope: {
    kind: 'global' | 'group' | 'control';
    groupId: number | null;
    controlKind: ControlKind | null;
    seriesKey: string | null;
  };
  thresholdPct: number;
  corpus: {
    total: number;
    labelled: number;
    withIncoherence: number;
    truthful: number;
    evaluated: number;
    /** Labelled videos whose run never ran the coherence engine. Out of every denominator. */
    coherenceNotRequested: number;
  };
  /** The anti-survivorship counts, beside the headlines and never behind a tab. */
  buckets: {
    ok: number;
    notAnalysed: number;
    degraded: number;
    notRequested: number;
  };
  methods: Record<string, MethodDelta>;
}

/**
 * Which run of each video the coherence metrics score.
 *
 * `latest` is its latest run that succeeded, whatever launched it — a group, a folder, a replay or
 * an automatic retry. Absent is upstream's older reading: the run each video shows.
 */
export type MetricsResolution = 'latest';

/** What the coherence metrics are asked for. Absent fields are upstream's defaults. */
export interface CoherenceScope {
  groupId?: number;
  /** A control's videos instead of a group's: the hub's Veracidad tile reads the coherence control. */
  controlKind?: ControlKind;
  threshold?: number;
  resolution?: MetricsResolution;
}

// ---------------------------------------------------------------------------
// One analysis of one video (HU-33 to HU-36)
// ---------------------------------------------------------------------------

/**
 * The header of a review: what to play, and which run produced what is shown.
 *
 * `videoUrl` is signed and dies in two hours, with no renewal endpoint. A long review session
 * will see it expire, so the screen has to be able to ask again rather than hold it for the
 * session.
 */
export interface AnalysisMetadata {
  videoUrl: string | null;
  video: {
    id: number;
    name: string;
    description: string | null;
    createdAt: Date | null;
    durationSeconds: number;
    status: string | null;
    /**
     * How long upstream took to analyse it — not how long the video is.
     *
     * The two travelled under one name until 74 seconds of processing were drawn as the length of
     * a 28-second clip. Null when the row never recorded both instants.
     */
    processingSeconds: number | null;
    analysisStartedAt: Date | null;
    analysisCompletedAt: Date | null;
  };
  analysisStatus: string | null;
  analysisProgress: number | null;
  run: {
    runId: string | null;
    status: RunStatus | null;
    attempt: number;
    enginesRequested: string[];
    enginesExecuted: string[];
    enginesSkipped: Array<{ name: string; reason: string }>;
    engine: EngineVersions | null;
    config: RunConfigRef | null;
    /** The one the video's pages and metrics use. Reviewing any other is reviewing history. */
    isActive: boolean;
    /** It finished, but an engine was skipped or a stage failed on the way. */
    partial: boolean;
    batch: string | null;
    note: string | null;
    startedAt: Date | null;
    finishedAt: Date | null;
    stages: RunStage[];
  } | null;
  /**
   * The identity stamped on the result document itself.
   *
   * The only configuration identity a run from before the ledger has: its run block carries no
   * engine and, often, no configuration. Null when the answer does not carry the block at all.
   */
  provenance: AnalysisProvenance | null;
  /**
   * Whether this run still keeps its landmarks.
   *
   * False is the common case for anything older than a few weeks: they are the first thing the
   * collector removes. The screen says so and offers to pin the run, rather than drawing an empty
   * overlay that reads as a bug.
   */
  landmarksRetained: boolean;
  landmarksConfig: { totalFrames?: number; fps?: number } | null;
}

/**
 * The provenance stamp of a result document.
 *
 * Every field nullable, because the worker started writing them one release at a time: a document
 * from before that reads as «unknown», never as a zero or an empty hash.
 */
export interface AnalysisProvenance {
  schemaVersion: string | null;
  analysisConfigId: number | null;
  analysisConfigVersion: number | null;
  analysisConfigHash: string | null;
  enginesHash: string | null;
  degraded: boolean | null;
  engine: EngineVersions | null;
}

/** One detected gesture episode. */
export interface GestureEpisode {
  episodeId: string;
  gestureName: string;
  startMs: number;
  endMs: number;
  durationMs: number;
  /** `gesture` is an instant, `sustained` a posture that held. */
  classification: string;
  shouldShow: boolean;
  /**
   * Whether the engine counted this one.
   *
   * `alert` or `discarded`, and it is the explicit answer — `classification` never says
   * «discarded», whatever its name suggests. On a real analysis seventy of a hundred episodes are
   * discarded, so reading the wrong field here is the difference between a full feed and an empty
   * one.
   */
  analysisStatus: string | null;
  /** Against this person's own baseline, or against an absolute count. */
  analysisMethod: string | null;
  discardReason: string | null;
  /** `rare`, `intermediate`, `habitual` — how often this person does this. Null when not measured. */
  frequencyBand: string | null;
  absoluteCount: number | null;
  /** The detector's confidence across the episode, which is a range rather than a number. */
  confidence: { mean: number; min: number; max: number } | null;
}

export interface GestureAlert {
  episodeId: string;
  gestureName: string;
  timestampMs: number;
  durationMs: number;
  /**
   * The engine's one word for why this reads as it does.
   *
   * Does double duty: the frequency band for a detection that counted, the cause for one that was
   * dropped. `rare`, `intermediate`, `habitual`, `at_baseline`, `sustained_not_valid`,
   * `suppressed_subject_speech`. A `too_short` discard (gestures 4.1.0) keeps its habituality
   * reason here and says `too_short` in `discardReason`.
   */
  reason: string;
  /** `spontaneous` or prompted. */
  gestureKind: string | null;
  isSubjectSpeaking: boolean | null;
  /**
   * The engine's own working, passed through.
   *
   * Everything the detail view shows under «Métricas» comes from here, and none of it is worth a
   * field of its own on this side: it is the engine's bag, it grows every release, and the view
   * renders whatever is in it.
   */
  explain: GestureExplain | null;
}

/** The engine's working for one alert. Every field optional: it is a bag, not a contract. */
export interface GestureExplain {
  confidence?: number;
  inBaseline?: boolean;
  overlapsSubjectSpeech?: boolean;
  overlapsInterviewerSpeech?: boolean;
  isSubjectSpeaking?: boolean;
  analysisMethod?: string;
  frequencyBand?: string;
  absoluteCount?: number;
  localFreq?: number;
  globalFreq?: number;
  localEpisodeCount?: number;
  suppressedReason?: string | null;
  notes?: string | null;
  featureWeights?: Record<string, number>;
}

/**
 * One coherence alert: a gesture that reached the semantic layer, and what it cost.
 *
 * `reductionPct` of 0 with `matched: true` is the case the interface must not flatten: the rule
 * fired and subtracted nothing. It is neither a detection nor a silence.
 */
export interface CoherenceAlert {
  alertId: string;
  episodeId: string | null;
  gestureName: string;
  timestampMs: number;
  ruleId: string | null;
  ruleLabel: string | null;
  matched: boolean;
  /** What it actually subtracted. */
  reductionPct: number;
  /** What the rule *would* subtract. Lets the interface say «-80 % (no aplicado)». */
  potentialReductionPct: number;
  reasonCategory: string | null;
  confidence: number | null;
  windowSeconds: number | null;
  /**
   * Where the weight came from: the ruleset, or the deprecated per-rule override.
   *
   * Written by the engine only since late September 2026, so every older alert has none — which
   * says nothing about the source, and must not be shown as «el catálogo de reglas».
   */
  reductionSource: string | null;

  // Everything below is optional so an alert written by hand — in a spec, in a fake — keeps
  // compiling. The gateway always fills them; `undefined` only ever means «not built by it».

  /** The idea's score before and after this alert. Their difference is what the panel adds up. */
  coherenceBefore?: number | null;
  coherenceAfter?: number | null;
  /** The classifier's reasoning, in the engine's words. */
  explanation?: string | null;
  /** What the classifier compared the gesture with. Null when the alert never reached it. */
  semanticMatch?: CoherenceSemanticMatch | null;
  /** The gesture's own interval: the durable coordinate a judgement is filed under. */
  gestureRangeMs?: [number, number] | null;
  /** Gesture instant minus the matched text's start. Negative when the gesture came first. */
  semanticTimingAlignmentMs?: number | null;
  /** The rule's source: which signal (`lips`) and which value of it (`lips_back`). */
  sourceItem?: string | null;
  sourceValue?: string | null;
  /** How the candidate text was chosen: `idea_block`, `window`, or `none` when there was none. */
  semanticCandidateSource?: string | null;
  gestureKind?: string | null;
  isSubjectSpeaking?: boolean | null;
  segmentStrategy?: string | null;
  /** The semantic blocks this alert belongs to, by strategy. */
  blockIds?: CoherenceAlertBlockIds;
  /**
   * The detector's working, which is not the classifier's.
   *
   * `confidence` here is the detector's and is usually null; the one on the alert itself is the
   * classifier's. Two numbers with one name, kept apart on purpose.
   */
  detectorExplain?: GestureExplain | null;
}

export interface CoherenceSemanticMatch {
  text: string;
  startMs: number | null;
  endMs: number | null;
  speaker: string | null;
}

export interface CoherenceAlertBlockIds {
  perAlert: string[];
  merged: string[];
  mergedParent: string[];
}

/** What a semantic alert is judged against: every origin but the gestures, which raise their own. */
export type SemanticAlertOrigin = Exclude<CoherenceRuleOrigin, 'gesture'>;

/**
 * What an idea contradicts: another idea of the same video, a line of the person's CV, something
 * the person said in another video, or nothing — a rule about the idea on its own.
 */
export type ContradictionKind = 'idea' | 'cv' | 'video' | 'context' | 'none';

export interface SemanticContradiction {
  kind: ContradictionKind;
  /** The other idea's text, or the line quoted from the CV or the other video. May be empty. */
  text: string;
  /** Only for another idea of this video: where it is, to seek to it. */
  blockId: string | null;
  startMs: number | null;
  endMs: number | null;
  /** Only for another video: its id. Null for a CV, which has none. */
  sourceId: string | null;
  sourceName: string | null;
}

/**
 * One semantic alert (coherence engine 7.0.0): a coherence rule that fired on an idea and what it
 * subtracts from it. One per idea and rule. The idea's scores already include it.
 */
export interface SemanticAlert {
  alertId: string;
  /** The idea it subtracts from. */
  blockId: string;
  ruleId: string;
  ruleLabel: string;
  origin: SemanticAlertOrigin;
  /** The idea's own text. */
  text: string;
  startMs: number | null;
  endMs: number | null;
  speaker: string;
  /** What the idea answers: the other speaker's last turn before it. Empty when there is none. */
  question: string;
  contradicts: SemanticContradiction;
  /** One line, in the judge's words. */
  explanation: string;
  /** 0 to 1. */
  confidence: number | null;
  /** 0-100: what it takes from the idea's coherence. */
  coherenceReduction: number;
}

/**
 * Whether a run judged a coherence rule. `no_origin` is an enabled rule the run carried nothing to
 * judge against: a video without a person, a person without CV or without other transcribed videos.
 */
export type CoherenceRuleStatus = 'active' | 'disabled' | 'no_origin';

export interface CoherenceRuleRun {
  ruleId: string;
  label: string;
  origin: CoherenceRuleOrigin;
  /** 0-100. Always 0 for the gesture rule, which subtracts what its gesture rules say. */
  reductionPct: number;
  status: CoherenceRuleStatus;
  /** Semantic alerts the rule raised in this run. */
  alerts: number;
  /**
   * Engine 7.3.0 (null on an older run, and on the gesture rule): the least confidence it was
   * judged with, what it compared with, its calls to the LLM (0 or 1), the findings the LLM
   * returned and how many of them were discarded.
   */
  minConfidence?: number | null;
  compareWith?: CoherenceCompareWith | null;
  calls?: number | null;
  findings?: number | null;
  discarded?: number | null;
}

/** Why a finding of the LLM raised no alert (engine 7.3.0). */
export type RuleFindingReason =
  | 'not_subject'
  | 'low_confidence'
  | 'duplicate'
  | 'no_reference'
  | 'no_other_idea'
  | 'unknown_idea';

/**
 * One finding the LLM returned for a rule (engine 7.3.0), kept — it raised the alert — or
 * discarded with its reason. The idea's block and times are null when the idea does not exist.
 */
export interface RuleFinding {
  ruleId: string;
  ideaIndex: number;
  blockId: string | null;
  startMs: number | null;
  endMs: number | null;
  text: string;
  contradicts: SemanticContradiction;
  explanation: string;
  confidence: number | null;
  status: 'kept' | 'discarded';
  reason: RuleFindingReason | null;
}

/** `notes.coherence`: what the run did with its coherence rules. */
export interface CoherenceRulesNotes {
  thresholdPct: number | null;
  /** The person the video shows, when it has one: where the CV and the other videos come from. */
  personId: number | null;
  semanticAlerts: number | null;
  rules: CoherenceRuleRun[];
  /** Every finding of the LLM (engine 7.3.0). Null on a run before it, which recorded none. */
  ruleFindings?: RuleFinding[] | null;
}

export interface GestureAnalysis {
  episodes: GestureEpisode[];
  alerts: GestureAlert[];
  coherenceAlerts: CoherenceAlert[];
  baselineByGesture: Record<string, number>;

  // Optional for the same reason as the new fields of `CoherenceAlert`: an analysis built by hand
  // with the four keys above keeps compiling. The gateway always sets them.

  /** The semantic layer of this run. Null when the document carries no coherence at all. */
  coherence?: CoherenceLayer | null;
  /**
   * The semantic alerts: coherence rules of origin transcript, CV or dataset that fired on an idea.
   * Empty on a document written before engine 7.0.0, which has none.
   */
  semanticAlerts?: SemanticAlert[];
  /** The coherence settings sealed into the document. Null when it predates the snapshot. */
  coherenceConfig?: CoherenceConfig | null;
  /** The document's own provenance stamp. */
  provenance?: AnalysisProvenance | null;
  /**
   * Which engines this document says it ran.
   *
   * Null on documents from before the engine selection existed — which is not «none ran», and
   * the difference is the whole of why a legacy run once read as having skipped coherence.
   */
  enginesRun?: EnginesRun | null;
}

export interface EnginesRun {
  requested: string[];
  executed: string[];
  skipped: Array<{ name: string; reason: string }>;
  partial: boolean;
}

/** What the coherence engine left in one result document. */
export interface CoherenceLayer {
  rulesetVersion: string | null;
  notes: CoherenceNotes | null;
  /** The diarised transcript and its blocks. Null when the engine built none. */
  diarization: SemanticLayer | null;
}

/**
 * The engine's own tally of the run.
 *
 * The only honest source for the funnel. `alerts[]` holds every episode, discarded ones included,
 * so counting it reads «298 alerts» where the engine considered 37.
 */
export interface CoherenceNotes {
  alertsTotal: number | null;
  coherenceRows: number | null;
  alertsWithoutRuleBinding: number | null;
  duplicateSignalRows: number | null;
  transcriptionSegments: number | null;
  bulkBatches: number | null;
  bulkItems: number | null;
  segmentStrategy: string | null;
  candidatesSummary: CandidatesSummary | null;
  /**
   * What the run did with its coherence rules. Null on a document written before engine 7.0.0;
   * optional so notes built by hand keep compiling, as the gateway always sets it.
   */
  coherence?: CoherenceRulesNotes | null;
}

export interface CandidatesSummary {
  thresholdPct: number | null;
  ideaBlocksTotal: number | null;
  belowThresholdSumReductionTotal: number | null;
  belowThresholdAlertMajorTotal: number | null;
  candidates: Array<{
    blockId: string;
    startMs: number | null;
    endMs: number | null;
    text: string;
    scoreComputedSubtractionAfter: number | null;
    scoreAlertMajorAfter: number | null;
    gestures: Array<{
      gestureName: string;
      ruleId: string | null;
      coherenceReduction: number | null;
      frequencyBand: string | null;
      reason: string | null;
    }>;
  }>;
}

/** One diarised word. Already in milliseconds on the wire, unlike `/transcription`. */
export interface SemanticWord {
  index: number;
  text: string;
  startMs: number;
  endMs: number;
  /** The diarisation's label (`speaker_0`), not a role. Empty when it could not tell. */
  speaker: string;
  segmentIndex: number | null;
}

/**
 * One block of the semantic layer, whatever the strategy that cut it.
 *
 * The same shape serves ideas, per-alert blocks, merged overlaps and the ungrouped list; which one
 * it is lives in `blockKind`. Scores are nullable because a block cut on this side carries only
 * what could be computed from its alerts.
 */
export interface SemanticBlock {
  blockId: string;
  startMs: number | null;
  endMs: number | null;
  text: string;
  speaker: string;
  sourceSegmentIndex: number | null;
  contributingAlertIds: string[];
  highlightWordIndexes: number[];
  anchorWordIndex: number | null;
  blockKind: string;
  parentBlockId: string | null;
  childBlockIds: string[];
  /** «Resta computada»: a hundred minus what every alert of the block subtracted. */
  scoreComputedSubtractionAfter: number | null;
  /** The worst single alert of the block. */
  scoreAlertMajorAfter: number | null;
  aggregateCoherenceAfter: number | null;
  aggregateAlertCount: number | null;
  aggregateMatchedCount: number | null;
  /** The engine's own verdict against the idea threshold. Null when the block carries no flag. */
  belowThresholdSumReduction: boolean | null;
  belowThresholdAlertMajor: boolean | null;
  /**
   * The semantic alerts that subtract from this idea; its scores above already include them.
   * Absent on a block built by hand or cut on this side, empty on a document before engine 7.0.0.
   */
  semanticAlertIds?: string[];
}

export interface SemanticLayer {
  /** Sorted by index, then start: the order the karaoke reads them in. */
  words: SemanticWord[];
  /** A name per diarisation label, when the engine could infer one. */
  speakerNames: Record<string, { name: string; confidence: number | null }>;
  /**
   * The engine's ideas. Null when the document has no list of them, which is not the same as an
   * empty one: the first has no verdict, the second is a run that cleared.
   */
  blocksIdea: SemanticBlock[] | null;
  blocksPerAlert: SemanticBlock[];
  /** Children and parents of the merged-overlap strategy, as the engine separates them. */
  blocksMerged: SemanticBlock[];
  blocksMergedParents: SemanticBlock[];
  /** Null when the document has no list of its own and it has to be derived from the alerts. */
  blocksNone: SemanticBlock[] | null;
}

/**
 * The coherence engine's settings as the run sealed them.
 *
 * `rules` reuses the ruleset's model — but note the unit: a rule's `reduction` is the engine's
 * fraction (0.8), while an alert's `potentialReductionPct` is already a percentage (80).
 */
export interface CoherenceConfig {
  thresholds: { good: number; warn: number };
  /** Whether the thresholds above are the product defaults because the run sealed none. */
  thresholdsDefaulted: boolean;
  /** Under this score, an idea counts as subtracting for the run's verdict. */
  ideaThresholdPct: number;
  ideaThresholdDefaulted: boolean;
  segmentStrategy: string | null;
  semanticWindowSeconds: number | null;
  transcriptionProvider: string | null;
  rulesetVersion: string | null;
  rules: GestureRule[];
}

export interface TranscriptSegment {
  startMs: number;
  endMs: number;
  speaker: string;
  text: string;
}

export interface TranscriptWord {
  word: string;
  startMs: number;
  endMs: number;
  speaker: string;
}

export interface Transcription {
  segments: TranscriptSegment[];
  words: TranscriptWord[];
}

/** Why a transcription failed: upstream's code (`transcription_failed`…) and its own sentence. */
export interface TranscriptionError {
  type: string;
  message: string;
}

/**
 * A video's own transcription (product owner, 2026-09-28).
 *
 * Made once per video, by a job of its own, when the video is uploaded — not by an analysis, so it
 * is there to label against before any run, and coherence reads it instead of transcribing again.
 * `segments` and `words` are empty until it is `done`, and stay empty for a video without speech.
 */
export interface VideoTranscription {
  videoId: number;
  status: TranscriptionStatus;
  /** Jobs asked for so far: a retry is one more. */
  attempts: number;
  requestedAt: Date | null;
  startedAt: Date | null;
  finishedAt: Date | null;
  provider: string | null;
  model: string | null;
  language: string | null;
  segments: TranscriptSegment[];
  words: TranscriptWord[];
  /** Only when it failed. */
  error: TranscriptionError | null;
}

/** A frame of landmarks, in normalised coordinates so the canvas scales with the video. */
export interface LandmarkFrame {
  timestampMs: number;
  /** MediaPipe's face mesh: 478 points, normalised 0–1 against the frame. */
  face: Array<{ x: number; y: number }>;
  /**
   * The two hands, apart.
   *
   * Not one joined array, which is what this was at first. The overlay draws a hand as a skeleton
   * of twenty-three segments indexed 0–20 *within that hand*, so joined, every segment past the
   * first hand points at the wrong knuckle and the drawing sprouts lines between the two.
   *
   * Empty is the common case: a seated interview keeps at least one hand out of shot most of the
   * time, and some analyses track no hands at all.
   */
  leftHand: Array<{ x: number; y: number }>;
  rightHand: Array<{ x: number; y: number }>;
}

export interface LandmarkWindow {
  frames: LandmarkFrame[];
  startSeconds: number;
  endSeconds: number;
}

/** One moment of a video somebody marked as an incoherence. */
export interface IncoherenceMark {
  id: number;
  startMs: number;
  endMs: number;
  labeledBy: string | null;
  createdAt: Date | null;
}

/** `unlabelled`: no range and not complete — the video every metric leaves out. */
export type VideoLabellingState = VideoLabel | 'unlabelled';

/**
 * A video's truth labelling: the ground truth the veracity engine (`coherence` upstream) is graded
 * against. Any tramo de mentira makes the video `incoherent` («Mentira»); marked complete with none,
 * `coherent` («Verdad»); neither, `unlabelled` («Sin etiquetar»).
 */
export interface VideoLabelling {
  videoId: number;
  state: VideoLabellingState;
  complete: boolean;
  completedBy: string | null;
  completedAt: Date | null;
  /** In the order of the video. */
  ranges: IncoherenceMark[];
}

/** Where a person saw the eyes go, for a `gaze_deviation` label: the engine's own buckets. */
export type GazeDirection =
  'up_left' | 'up' | 'up_right' | 'left' | 'right' | 'down_left' | 'down' | 'down_right';

/**
 * Which labelling of a video a document is (product owner, 2026-10-29): `gestures`, every gesture
 * the person saw — the detection truth —; `gesture_alerts`, only the gestures they would raise as an
 * alert — the alerting truth, what Alertas gestuales measures the engine's alerts against. Each
 * scope is its own document with its own «complete», labelled on its own screen.
 */
export type GestureLabelScope = 'gestures' | 'gesture_alerts';

/**
 * One gesture a person saw in a video, and when (product owner, 2026-09-28).
 *
 * It belongs to the video, not to a run: it is the answer key the gestures engine is measured
 * against, and what coherence reads instead of the engine in a control run. Same clock as the
 * engine's episodes — the video's, in milliseconds.
 */
export interface GestureLabel {
  labelId: string;
  gestureName: string;
  startMs: number;
  endMs: number;
  /** Only for `gaze_deviation`; null for every other gesture. */
  gazeDirection: GazeDirection | null;
  labeledBy: string | null;
  createdAt: Date | null;
  updatedAt: Date | null;
}

/**
 * Every gesture label of one video, and whether its labelling is complete: every gesture of the
 * video is labelled. `complete` is what lets a video count for the control groups; a label written
 * later does not reset it.
 */
export interface VideoGestureLabels {
  scope: GestureLabelScope;
  sourceType: 'manual_analysis';
  sourceId: number;
  labels: GestureLabel[];
  complete: boolean;
  completedBy: string | null;
  completedAt: Date | null;
  updatedBy: string | null;
  updatedAt: Date | null;
  /** Grows on every write; 0 when nobody has labelled the video yet. */
  version: number;
}

/** What a label is written with. The gaze direction only travels for `gaze_deviation`. */
export interface GestureLabelDraft {
  gestureName: string;
  startMs: number;
  endMs: number;
  gazeDirection: GazeDirection | null;
}

export type FeedbackTarget = 'gesture' | 'idea_verdict' | 'idea_alert';

export type FeedbackVerdict = 'correct' | 'incorrect' | 'missing';

export type FeedbackErrorType =
  'over_detected' | 'under_detected' | 'wrong_label' | 'wrong_timing' | 'other';

/** Whether a wrong alert is the detector's fault or the method's. The legacy asks it per review. */
export type FeedbackCause = 'technical' | 'methodological';

/**
 * A judgement about what one analysis produced.
 *
 * `runId` is required, not optional: a judgement belongs to the analysis it is about. The legacy
 * omits it, which is why every judgement written so far is attached to whichever run happened to
 * be active instead of the one being reviewed.
 *
 * `targetRef` is the stable coordinate — gesture and interval — because a detection's id is minted
 * per run and a judgement keyed on one would attach to nothing after a re-analysis.
 */
export interface FeedbackDraft {
  videoId: number;
  runId: string;
  targetType: FeedbackTarget;
  targetRef: string;
  verdict: FeedbackVerdict;
  gestureName?: string;
  startMs?: number;
  endMs?: number;
  errorType?: FeedbackErrorType;
  cause?: FeedbackCause;
  expectedTimestampMs?: number;
  note?: string;
  ideaId?: string;
}

export interface DetectionFeedback extends Omit<FeedbackDraft, 'videoId'> {
  id: number;
  createdAt: Date | null;
}

// ---------------------------------------------------------------------------
// Engine versions
// ---------------------------------------------------------------------------

/**
 * One published version of an engine, and what changed in it.
 *
 * The changes are English on purpose: they are written once, next to the engine's code, and shown
 * as they are. `date` is a calendar day (`YYYY-MM-DD`), not an instant, so it stays a string.
 */
export interface EngineRelease {
  version: string;
  date: string | null;
  changes: string[];
}

/** Every version of one engine, newest first. Any engine upstream documents, not only two. */
export interface EngineChangelog {
  engine: string;
  releases: EngineRelease[];
}

// ---------------------------------------------------------------------------
// One execution, video by video
// ---------------------------------------------------------------------------

/** How one video scored under one score method, as upstream worked it out. */
export interface ExecutionVideoScore {
  /** `incoherent_detected`, `incoherent_missed`, `false_positive` or `truthful_cleared`; kept as upstream says it. */
  outcome: string;
  /** Null when its analysis could not be read. */
  correct: boolean | null;
  /** An idea flagged outside any lie: every one on a truthful video, the ones off the lie on a lie. */
  falsePositive: boolean;
  subtractingIdeas: number;
  /** Null on a truthful video — there is no lie to fall into — and with no analysis. */
  overlappingIdeas: number | null;
  incoherenceIdeas: number | null;
  falsePositiveIdeas: number;
}

/**
 * One video of an execution, as its metrics counted it — or left it out. `methods` is empty on a
 * video that was not scored: one with no label, or whose run there did not end well; `reason` then
 * says why, in upstream's word.
 */
export interface ExecutionVideo {
  sourceType: GroupSourceType;
  sourceId: number;
  name: string | null;
  label: VideoLabel | null;
  /** The run it was read by in this execution. */
  runId: string | null;
  /** `available`, `not_requested` or `missing`; null where its run has no result to look in. */
  coherenceStatus: string | null;
  reason: string | null;
  methods: Record<string, ExecutionVideoScore>;
}

export interface ExecutionVideos {
  /** Whose execution it is: a group, by its id, or a control, by its kind. Never both. */
  groupId: number | null;
  controlKind: ControlKind | null;
  /** The execution upstream says it answered for; null is its older reading across executions. */
  batch: string | null;
  thresholdPct: number | null;
  videos: ExecutionVideo[];
}

// ── «Metodología»: what a video has for each origin, and the quick simulation ──

/** What each origin of the coherence rules has for a video (contract «Metodología», 2026-10-14). */
export interface VideoSources {
  videoId: number;
  person: VideoPerson | null;
  /** The stored analysis the counts come from: the video's current result. Null without one. */
  sourceResultDocId: string | null;
  /** Gesture alerts of the current analysis; null without one. */
  gestures: number | null;
  /** Ideas of the current analysis; null without one with coherence. */
  ideas: number | null;
  /** The person's other videos with a transcription. */
  otherVideos: number;
  /** The person's analysed CVs. */
  cvs: number;
  contexts: number;
}

export type SimulationStatus = 'QUEUED' | 'RUNNING' | 'SUCCEEDED' | 'FAILED';

export interface SimulationFailure {
  type: string;
  message: string;
  /** Upstream's reading: a worker before the job takes it and does nothing with it. */
  hint: string | null;
}

/** One rule in a simulation: whether it was judged, and what it did. */
export interface SimulationRuleResult {
  ruleId: string;
  status: CoherenceRuleStatus;
  /** Calls to the LLM: 1 for a judged rule, 0 for the gesture rule or one not judged. */
  calls: number;
  alerts: number;
  findings: number;
  discarded: number;
  /** Coherence points it took, summed over every idea. */
  subtractionPct: number;
}

export interface SimulationIdea {
  blockId: string;
  text: string;
  startMs: number | null;
  /** The interviewed person's; false is the interviewer. */
  subject: boolean;
  coherenceAfter: number | null;
  incoherent: boolean;
  gestureAlertIds: string[];
  semanticAlertIds: string[];
}

export interface SimulationAlert {
  alertId: string;
  blockId: string;
  ruleId: string;
  /** 0-100. */
  coherenceReduction: number;
}

export interface SimulationResult {
  rules: SimulationRuleResult[];
  ideas: SimulationIdea[];
  semanticAlerts: SimulationAlert[];
  thresholdPct: number | null;
  engineVersion: string | null;
  totalCalls: number;
  /** Every finding of the LLM, kept or discarded; null when the engine sent none. */
  ruleFindings: RuleFinding[] | null;
}

/** «Simular»: the coherence rules of the draft on one video, never its result. */
export interface CoherenceSimulation {
  id: string;
  status: SimulationStatus;
  videoId: number;
  createdAt: Date | null;
  finishedAt: Date | null;
  /** Its place in the queue while it waits. */
  position: number | null;
  error: SimulationFailure | null;
  result: SimulationResult | null;
}
